import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useRef, useState } from 'react';
import Observer from '@researchgate/react-intersection-observer';
import classnames from 'classnames';
import parseClassnames from "../helpers/parseClassnames";
import { DEFAULT_PLACEHOLDER } from "./constants";
import getImgxUrl from "./getImgxUrl";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Requeriments:
 * - It must have a better api
 * - It must use picture, source and image html5 tags
 * - it must lazy load the images
 * - it must handle the dpr x1 and x2
 * - it must add a "progressive animation" (TODO)
 * - it must use auto=compress (this will serve the image in webp if the browser supports it)
 *
 *
 * ==== Animation ====
 * For the animation we need to use the onload event.
 * 1. Show the placeholder
 * 2. Detect the image is in the viewport (using intersection observer) and then start to load the real image.
 * 3. Once the real image is loaded (using the onload event) we change its opacity and transform css properties
 * 4. Hide the placeholder and show the real one.
 *
 * ==== Use will-transform ====
 * We are going to set the will-transform css property dynamically.
 * This property is used to help the browser to optimize the relayout/repaint of the elements.
 * 1. One the image has been "pre-loaded",  set the will-transform property to the picture element
 * 2. After 300ms (when the animation ends), remove the will-transform property
 */

var optimizedImage = function optimizedImage(src) {
  return "".concat(src, "&dpr=1 1x, ").concat(src, "&dpr=2 2x");
};

/**
 * This component must be used only in next.js application.
 */
var LazyImage = function LazyImage(_ref) {
  var placeholder = _ref.placeholder,
    src = _ref.src,
    imgxParams = _ref.imgxParams,
    observerProps = _ref.observerProps,
    style = _ref.style,
    imgStyle = _ref.imgStyle,
    alt = _ref.alt,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$imgClassName = _ref.imgClassName,
    imgClassName = _ref$imgClassName === void 0 ? '' : _ref$imgClassName,
    _ref$backgroundImage = _ref.backgroundImage,
    backgroundImage = _ref$backgroundImage === void 0 ? '' : _ref$backgroundImage,
    _ref$pictureStyles = _ref.pictureStyles,
    pictureStyles = _ref$pictureStyles === void 0 ? {} : _ref$pictureStyles,
    _ref$customOptimizedI = _ref.customOptimizedImage,
    customOptimizedImage = _ref$customOptimizedI === void 0 ? '' : _ref$customOptimizedI,
    dark = _ref.dark,
    isDark = _ref.isDark,
    isTransparent = _ref.isTransparent;
  var imageRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isIntersecting = _useState2[0],
    setIsIntersecting = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isResourceLoaded = _useState4[0],
    setIsResourceLoaded = _useState4[1];
  var handleIntersection = function handleIntersection(event, unobserve) {
    if (event.isIntersecting) {
      setIsIntersecting(true);
      unobserve();
    }
  };
  var options = _objectSpread({
    onChange: handleIntersection,
    rootMargin: '0px 0px 300px 0px',
    disabled: isResourceLoaded
  }, observerProps);
  var webpImage = getImgxUrl(src, _objectSpread({
    auto: 'compress',
    fm: 'webp'
  }, imgxParams));
  var avifImage = getImgxUrl(src, _objectSpread({
    // avif is the default format if the browser supports it
    auto: 'format'
  }, imgxParams));
  var defaultPlaceholderTheme = isDark || dark ? 'dark' : 'light';
  if (isTransparent) {
    defaultPlaceholderTheme = 'transparent';
  }
  var placeholderImage = placeholder || DEFAULT_PLACEHOLDER[defaultPlaceholderTheme];
  var defaultImage = getImgxUrl(src, imgxParams);
  return /*#__PURE__*/_jsx(Observer, _objectSpread(_objectSpread({}, options), {}, {
    children: /*#__PURE__*/_jsxs("div", {
      className: parseClassnames(className, classnames("block max-w-full h-auto", {
        relative: !className.includes('absolute')
      })),
      style: style,
      children: [/*#__PURE__*/_jsx("div", {
        className: "absolute inset-0 z-10 ".concat(imgClassName),
        style: _objectSpread(_objectSpread({}, imgStyle), {}, {
          opacity: isResourceLoaded ? 0 : 1,
          backgroundImage: backgroundImage || "url(".concat(placeholderImage, ")"),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'opacity 0.3s ease-in-out',
          transitionDelay: '100ms',
          willChange: isResourceLoaded ? 'unset' : 'opacity'
        })
      }), isIntersecting && /*#__PURE__*/_jsxs("picture", {
        className: "absolute inset-0",
        style: pictureStyles,
        children: [/*#__PURE__*/_jsx("source", {
          srcSet: customOptimizedImage || optimizedImage(avifImage),
          type: "image/avif"
        }), /*#__PURE__*/_jsx("source", {
          srcSet: customOptimizedImage || optimizedImage(webpImage),
          type: "image/webp"
        }), /*#__PURE__*/_jsx("img", {
          ref: imageRef,
          className: parseClassnames(imgClassName, 'w-full h-full block relative object-contain'),
          src: defaultImage,
          alt: alt,
          style: _objectSpread(_objectSpread({}, imgStyle), {}, {
            opacity: isResourceLoaded ? 1 : 0,
            transition: 'opacity 300ms ease-in-out'
          })
          // this little one is the trick
          ,
          onLoad: function onLoad() {
            return setIsResourceLoaded(true);
          },
          decoding: "async"
        })]
      })]
    })
  }));
};
var ImageTestComponent = function ImageTestComponent(_ref2) {
  var src = _ref2.src,
    alt = _ref2.alt;
  return /*#__PURE__*/_jsx("div", {
    role: "img",
    children: /*#__PURE__*/_jsx("img", {
      alt: alt,
      src: src,
      decoding: "async"
    })
  });
};
export default process.env.NODE_ENV === 'test' ? ImageTestComponent : LazyImage;